import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { Screen } from '../components/Screen'
import { Field } from '../components/Form'
import { EmptyState, ErrorState, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useClubOverview, usePaymentHistory, useSubscription, useTierPreview } from '../data/hooks'
import { clearPendingPayment, loadPendingPayment, savePendingPayment } from '../data/pendingPayment'
import { formatDayShort, parseIsoDate } from '../data/schedule'
import {
  LIMIT_LABEL,
  STATE_LABEL,
  TRIAL_AFTER_TEXT,
  TRIAL_AI_REQUESTS,
  daysLabel,
  discounted,
  limitText,
  overLimits,
  planLimit,
  planOf,
  plans,
  plansRulesText,
  rub,
  tierName,
  trialLostText,
  trialStartsOn,
} from '../data/subscription'
import {
  SliceApiError,
  type PaymentInfo,
  type PaymentStatus,
  type SubscriptionLimitKey,
  type SubscriptionSummary,
  type TierChangePreview,
} from '../data/types'
import { subscribeResume } from '../platform/platform'
import { useBackTo } from './AthleteScreen'

/**
 * Подписка клуба (S19, SC18): тариф, срок, лимиты, сравнение тарифов, смена
 * и продление с оплатой, промокод, автопродление, история платежей.
 *
 * Правила (ТЗ стр. 22, 24): смотреть может любой участник, менять и платить -
 * только владелец клуба (так же решает сервер). Просмотр тарифа ничего не
 * меняет и денег не списывает: смена - после расчёта и явного подтверждения.
 * Успех оплаты показываем только по ответу сервера, не по возврату со страницы
 * оплаты.
 */

function dateLabel(iso: string | null | undefined): string {
  const d = parseIsoDate(iso?.slice(0, 10))
  return d ? `${formatDayShort(d)} ${d.getFullYear()}` : ''
}

function timeLabel(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function Row({ title, meta, onClick }: { title: string; meta?: ReactNode; onClick?: () => void }) {
  const inner = (
    <>
      <span className="pc-list-row__main">
        <span className="pc-list-row__title" style={{ display: 'block' }}>
          {title}
        </span>
        {meta && <span className="pc-list-row__meta">{meta}</span>}
      </span>
      {onClick && <ChevronRight size={18} aria-hidden="true" className="pc-muted" />}
    </>
  )
  return onClick ? (
    <button type="button" className="pc-list-row" onClick={onClick}>
      {inner}
    </button>
  ) : (
    <div className="pc-list-row" style={{ cursor: 'default' }}>
      {inner}
    </div>
  )
}

function Kv({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pc-kv">
      <div className="pc-kv__label">{label}</div>
      <div className="pc-kv__value">{children}</div>
    </div>
  )
}

function usedText(used: number, max: number): string {
  if (max === -1) return `Использовано ${used}, без ограничения`
  if (used > max) return `Использовано ${used} из ${max}: больше лимита на ${used - max}`
  return `Использовано ${used} из ${max}${used === max ? '. Лимит исчерпан' : ''}`
}

/** «Тариф: PRO / Активна». У FREE состояния нет: он бессрочный. */
function badgeText(s: SubscriptionSummary): string {
  return s.state === 'free' ? tierName(s.paidTier) : `${tierName(s.paidTier)} / ${STATE_LABEL[s.state]}`
}

function periodText(s: SubscriptionSummary): string {
  switch (s.state) {
    case 'free':
      return 'Без срока окончания'
    case 'trial':
      return `Пробный период до ${dateLabel(s.expiresAt)}`
    case 'active':
      return s.expiresAt ? `Действует до ${dateLabel(s.expiresAt)}` : 'Без срока окончания'
    case 'expired':
      return `Срок истёк${s.expiresAt ? ` ${dateLabel(s.expiresAt)}` : ''}. Сейчас действуют лимиты FREE.`
    case 'cancelled':
      return 'Подписка отменена. Сейчас действуют лимиты FREE.'
  }
}

/** Что перестаёт работать при превышении: тот же текст для текущего превышения и для понижения тарифа. */
const OVER_LIMIT_RULE =
  'Ничего не удаляется. Новые записи этого вида создать не получится, а через 7 дней после превышения клуб перестанет сохранять новые данные (кроме отметок посещения), пока лишнее не удалено или не выбран тариф больше.'

function graceText(g: NonNullable<SubscriptionSummary['grace']>): string {
  const what = g.exceeded.map((k) => LIMIT_LABEL[k].toLowerCase()).join(', ')
  if (g.softLocked) {
    return `Превышены лимиты тарифа: ${what}. Клуб не сохраняет новые данные, кроме отметок посещения, пока лишнее не удалено или не выбран тариф больше.`
  }
  return `Превышены лимиты тарифа: ${what}. Через ${daysLabel(g.daysLeft)} клуб перестанет сохранять новые данные (кроме отметок посещения), если не удалить лишнее или не выбрать тариф больше.`
}

const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  pending: 'ждёт подтверждения',
  succeeded: 'оплачен',
  failed: 'не прошёл',
  canceled: 'отменён',
  unknown: 'статус неизвестен',
}

// ---------------------------------------------------------------------------
// S19 Подписка клуба
// ---------------------------------------------------------------------------

export function SubscriptionScreen() {
  const { api, toast, now } = useEnv()
  const navigate = useNavigate()
  const client = useQueryClient()
  const sub = useSubscription()
  const overview = useClubOverview()
  const back = useBackTo('/club')
  const [pending] = useState(() => loadPendingPayment(now().getTime()))
  const [sheet, setSheet] = useState<null | 'promo' | 'renewal'>(null)

  if (sub.isPending) {
    return (
      <Screen title="Подписка клуба" back={back}>
        <Skeleton rows={4} label="Загружаю подписку" />
      </Screen>
    )
  }
  if (sub.isError) {
    return (
      <Screen title="Подписка клуба" back={back}>
        <ErrorState title="Не удалось загрузить подписку" onRetry={() => void sub.refetch()} />
      </Screen>
    )
  }

  const s = sub.data
  const paid = s.paidTier !== 'free'
  const refresh = () => client.invalidateQueries({ queryKey: ['redesign', 'subscription'] })

  return (
    <Screen
      title="Подписка клуба"
      back={back}
      actionBar={
        s.canManage ? (
          <button
            type="button"
            className="pc-btn pc-btn--primary pc-btn--block"
            onClick={() => navigate('/club/subscription/plans')}
          >
            Сменить тариф
          </button>
        ) : undefined
      }
    >
      <span className="pc-badge" style={{ marginTop: 12 }}>
        {badgeText(s)}
      </span>
      <h2 className="pc-section-title" style={{ marginTop: 12 }}>
        {overview.data?.club.name ?? ' '}
      </h2>
      <p className="pc-muted" style={{ margin: 0 }}>
        {periodText(s)}
      </p>
      {s.state === 'trial' && (
        <p className="pc-muted" style={{ margin: '4px 0 0', fontSize: 14 }}>
          В пробном периоде - до {TRIAL_AI_REQUESTS} запросов Штаба в месяц. {TRIAL_AFTER_TEXT}.
        </p>
      )}

      {s.grace && (
        <div className="pc-alert" role="alert">
          {graceText(s.grace)}
        </div>
      )}

      {pending && (
        <div className="pc-note" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1 }}>
            Платёж за {tierName(pending.tier)} от {timeLabel(pending.createdAt)} ещё не подтверждён.
          </span>
          <button type="button" className="pc-btn" onClick={() => navigate(`/club/subscription/payment/${pending.id}`)}>
            Проверить
          </button>
        </div>
      )}

      <section className="pc-section" aria-labelledby="pc-sub-limits">
        <h2 id="pc-sub-limits" className="pc-section-title">
          Лимиты тарифа {tierName(s.tier)}
        </h2>
        <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
          {s.limits.map((l) => (
            <li key={l.key}>
              <Row title={LIMIT_LABEL[l.key]} meta={usedText(l.used, l.max)} />
            </li>
          ))}
        </ul>
      </section>

      {paid && <RenewalSection s={s} onChange={() => setSheet('renewal')} />}

      {s.canManage ? (
        <>
          {paid && (
            <button
              type="button"
              className="pc-btn pc-btn--block"
              style={{ marginTop: 16 }}
              onClick={() => navigate(`/club/subscription/change/${s.paidTier}`)}
            >
              Продлить {tierName(s.paidTier)} на 30 дней
            </button>
          )}
          <PromoSection s={s} onEnter={() => setSheet('promo')} />
          <PaymentsSection />
        </>
      ) : (
        <p className="pc-note">Тариф меняет и оплачивает владелец клуба.</p>
      )}

      <button
        type="button"
        className="pc-btn pc-btn--block"
        style={{ marginTop: 16 }}
        onClick={() => navigate('/club/subscription/plans')}
      >
        Тарифы и сравнение
      </button>

      {sheet === 'renewal' && (
        <RenewalSheet
          on={!s.autoRenewal}
          onClose={() => setSheet(null)}
          onDone={async (on) => {
            await api.setAutoRenewal(on)
            await refresh()
            setSheet(null)
            toast(on ? 'Автопродление включено' : 'Автопродление выключено')
          }}
        />
      )}
      {sheet === 'promo' && (
        <PromoSheet
          onClose={() => setSheet(null)}
          onApplied={async (r) => {
            await refresh()
            setSheet(null)
            toast(`Скидка ${r.percent}% до ${dateLabel(r.expiresAt)}`)
          }}
        />
      )}
    </Screen>
  )
}

function RenewalSection({ s, onChange }: { s: SubscriptionSummary; onChange: () => void }) {
  const price = planOf(s.paidTier)?.price ?? 0
  let text: string
  if (s.status === 'trial') {
    // Воркер автопродления берёт только оплаченные подписки (статус active или
    // expired): пробный период картой не продлевается.
    text = 'Пробный период сам не продлевается и ничего не списывает: чтобы остаться на тарифе, оплатите его.'
  } else if (s.autoRenewal && s.cardSaved) {
    text = `Включено: примерно за сутки до конца срока с сохранённой карты спишется ${rub(price)}. Подписка продлится на 30 дней, когда банк подтвердит оплату.`
  } else if (s.autoRenewal) {
    text =
      'Включено, но карта не сохранена: само ничего не спишется. Продлевайте вручную или оплатите с отмеченным автопродлением - тогда карта сохранится.'
  } else if (s.cardSaved) {
    text = 'Выключено: подписка закончится в срок, продление - вручную.'
  } else {
    text =
      'Выключено: подписка закончится в срок, продление - вручную. Чтобы включить, оплатите продление с отмеченным автопродлением - карта сохранится.'
  }
  // Включать без сохранённой карты бессмысленно: списывать не с чего
  const canToggle = s.canManage && s.status !== 'trial' && (s.autoRenewal || s.cardSaved)
  return (
    <section className="pc-section" aria-labelledby="pc-sub-renewal">
      <h2 id="pc-sub-renewal" className="pc-section-title">
        Автопродление
      </h2>
      <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
        {text}
      </p>
      {canToggle && (
        <button type="button" className="pc-btn" style={{ marginTop: 8 }} onClick={onChange}>
          {s.autoRenewal ? 'Выключить автопродление' : 'Включить автопродление'}
        </button>
      )}
    </section>
  )
}

function RenewalSheet({
  on,
  onClose,
  onDone,
}: {
  on: boolean
  onClose: () => void
  onDone: (on: boolean) => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const run = async () => {
    if (busy) return
    setBusy(true)
    setFailed(false)
    try {
      await onDone(on)
    } catch {
      setFailed(true)
      setBusy(false)
    }
  }
  return (
    <Sheet title={on ? 'Включить автопродление?' : 'Выключить автопродление?'} onClose={onClose}>
      <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
        {on
          ? 'Подписка будет продлеваться на 30 дней списанием с сохранённой карты, пока автопродление не выключено.'
          : 'Сохранённая карта будет отвязана и больше не будет списываться. Подписка закончится в срок, продлить можно вручную. Включить снова - оплатить с отмеченным автопродлением.'}
      </p>
      {failed && (
        <div className="pc-alert" role="alert">
          Не сохранилось. Попробуйте ещё раз.
        </div>
      )}
      <button type="button" className="pc-btn pc-btn--primary pc-btn--block" disabled={busy} onClick={run}>
        {busy ? 'Сохраняю…' : on ? 'Включить' : 'Выключить'}
      </button>
      <button type="button" className="pc-btn pc-btn--block" onClick={onClose}>
        Отмена
      </button>
    </Sheet>
  )
}

function PromoSection({ s, onEnter }: { s: SubscriptionSummary; onEnter: () => void }) {
  return (
    <section className="pc-section" aria-labelledby="pc-sub-promo">
      <h2 id="pc-sub-promo" className="pc-section-title">
        Промокод
      </h2>
      {s.discount ? (
        <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
          Скидка {s.discount.percent}%{s.discount.code ? ` по промокоду ${s.discount.code}` : ''}
          {s.discount.expiresAt ? ` до ${dateLabel(s.discount.expiresAt)}` : ''}. Применяется к платежам этого аккаунта.
        </p>
      ) : (
        <button type="button" className="pc-btn" onClick={onEnter}>
          Ввести промокод
        </button>
      )}
    </section>
  )
}

function PromoSheet({
  onClose,
  onApplied,
}: {
  onClose: () => void
  onApplied: (r: { percent: number; expiresAt: string }) => Promise<void>
}) {
  const { api } = useEnv()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apply = async () => {
    if (busy || !code.trim()) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.redeemPromo(code)
      await onApplied(r)
    } catch (e) {
      const err = e as SliceApiError
      setError(err?.kind === 'conflict' ? err.message : 'Не удалось проверить промокод. Попробуйте ещё раз.')
      setBusy(false)
    }
  }

  return (
    <Sheet title="Промокод" onClose={onClose}>
      <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
        Скидка начнёт действовать сразу и применится к следующей оплате тарифа.
      </p>
      <form
        className="pc-form"
        style={{ marginTop: 0 }}
        onSubmit={(e) => {
          e.preventDefault()
          void apply()
        }}
      >
        <Field label="Код" error={error ?? undefined}>
          {(p) => (
            <input
              {...p}
              className="pc-input"
              value={code}
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={50}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          )}
        </Field>
        <button type="submit" className="pc-btn pc-btn--primary pc-btn--block" disabled={busy || !code.trim()}>
          {busy ? 'Проверяю…' : 'Применить'}
        </button>
      </form>
      <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 8 }} onClick={onClose}>
        Отмена
      </button>
    </Sheet>
  )
}

function PaymentsSection() {
  const navigate = useNavigate()
  const history = usePaymentHistory()
  let body: ReactNode
  if (history.isPending) body = <Skeleton rows={1} label="Загружаю платежи" />
  else if (history.isError) body = <ErrorState title="Не удалось загрузить платежи" onRetry={() => void history.refetch()} />
  else if (!history.data.available) {
    body = (
      <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
        Оплата картой сейчас не подключена: платная смена и продление недоступны. Пробный период и переход на FREE работают.
      </p>
    )
  } else if (history.data.items.length === 0) {
    body = <EmptyState title="Платежей ещё не было" />
  } else {
    body = (
      <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
        {history.data.items.slice(0, 10).map((p) => (
          <li key={p.id}>
            <Row
              title={p.description || 'Оплата тарифа'}
              meta={`${dateLabel(p.paidAt ?? p.createdAt)}, ${rub(p.amount)}, ${PAYMENT_LABEL[p.status]}`}
              onClick={p.status === 'pending' ? () => navigate(`/club/subscription/payment/${p.id}`) : undefined}
            />
          </li>
        ))}
      </ul>
    )
  }
  return (
    <section className="pc-section" aria-labelledby="pc-sub-payments">
      <h2 id="pc-sub-payments" className="pc-section-title">
        Платежи
      </h2>
      {body}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Тарифы и сравнение
// ---------------------------------------------------------------------------

export function PlansScreen() {
  const navigate = useNavigate()
  const sub = useSubscription()
  const back = useBackTo('/club/subscription')

  if (sub.isPending) {
    return (
      <Screen title="Тарифы" back={back}>
        <Skeleton rows={4} label="Загружаю тарифы" />
      </Screen>
    )
  }
  if (sub.isError) {
    return (
      <Screen title="Тарифы" back={back}>
        <ErrorState title="Не удалось загрузить подписку" onRetry={() => void sub.refetch()} />
      </Screen>
    )
  }
  const s = sub.data
  const used = new Map(s.limits.map((l) => [l.key, l.used]))

  return (
    <Screen title="Тарифы" back={back}>
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        {plansRulesText(s.trialUsed)}
      </p>
      {s.discount && s.canManage && (
        <p className="pc-note">Ваша скидка {s.discount.percent}% по промокоду применится при оплате.</p>
      )}
      {!s.canManage && <p className="pc-note">Сменить тариф может владелец клуба.</p>}

      {plans().map((plan) => {
        const current = plan.id === s.paidTier
        const headingId = `pc-plan-${plan.id}`
        return (
          <section key={plan.id} className="pc-card" style={{ marginTop: 12 }} aria-labelledby={headingId}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 id={headingId} className="pc-section-title" style={{ margin: 0 }}>
                {plan.name}
              </h2>
              {current && <span className="pc-badge pc-badge--success">Текущий</span>}
            </div>
            <p style={{ margin: '4px 0 0', fontWeight: 650 }}>
              {plan.price === 0 ? 'Бесплатно' : `${rub(plan.price)} за 30 дней`}
            </p>
            {/* Строка о пробном периоде - только когда переход начнётся с него (то же правило, что на сервере) */}
            {trialStartsOn(s, plan) && (
              <p className="pc-muted" style={{ margin: '2px 0 0', fontSize: 14 }}>
                Начнётся с пробного периода: {daysLabel(plan.trialDays)} без оплаты, до {TRIAL_AI_REQUESTS} запросов Штаба
                в месяц
              </p>
            )}
            <ul className="pc-list" style={{ marginTop: 8 }}>
              {(Object.keys(LIMIT_LABEL) as SubscriptionLimitKey[]).map((key) => {
                const max = planLimit(plan, key)
                const have = used.get(key)
                const over = have !== undefined && max !== -1 && have > max
                return (
                  <li key={key}>
                    <div className="pc-list-row" style={{ cursor: 'default', minHeight: 0, padding: '6px 0' }}>
                      <span className="pc-list-row__main">{LIMIT_LABEL[key]}</span>
                      <span className="pc-list-row__aside" style={over ? { color: 'var(--pc-danger)' } : undefined}>
                        {limitText(max)}
                        {over ? `, у вас ${have}` : ''}
                      </span>
                    </div>
                  </li>
                )
              })}
            </ul>
            {s.canManage && !(current && plan.id === 'free') && (
              <button
                type="button"
                className={`pc-btn pc-btn--block${current ? '' : ' pc-btn--primary'}`}
                style={{ marginTop: 8 }}
                onClick={() => navigate(`/club/subscription/change/${plan.id}`)}
              >
                {current ? `Продлить ${plan.name}` : `Выбрать ${plan.name}`}
              </button>
            )}
          </section>
        )
      })}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Смена тарифа: расчёт -> подтверждение или оплата
// ---------------------------------------------------------------------------

/** Ошибка смены или создания платежа - текстом для человека. */
function changeErrorText(e: unknown): string {
  const err = e as SliceApiError
  if (err?.kind === 'network') return 'Нет связи: ничего не изменилось. Попробуйте ещё раз.'
  switch (err?.message) {
    case 'forbidden':
      return 'Сменить тариф может только владелец клуба.'
    case 'payments_unavailable':
      return 'Оплата картой сейчас не подключена: платёж не создан.'
    case 'payment_required':
      return 'Расчёт изменился: теперь смена платная. Проверьте условия ниже.'
    case 'no_payment_required':
      return 'Расчёт изменился: теперь смена без оплаты. Проверьте условия ниже.'
    case 'payment_pending':
      return 'Платёж за этот тариф с другими условиями ещё ждёт банка, новый не создан. Проверьте прежний: когда банк ответит по нему, можно оплатить снова.'
    default:
      return 'Не получилось: ничего не изменилось и ничего не списано. Попробуйте ещё раз.'
  }
}

export function TierChangeScreen() {
  const { tier = '' } = useParams()
  const plan = planOf(tier)
  const back = useBackTo('/club/subscription/plans')
  const navigate = useNavigate()

  if (!plan) {
    return (
      <Screen title="Смена тарифа" back={back}>
        <EmptyState
          title="Такого тарифа нет"
          action={
            <button type="button" className="pc-btn" onClick={() => navigate('/club/subscription/plans', { replace: true })}>
              К тарифам
            </button>
          }
        />
      </Screen>
    )
  }
  return <TierChange tier={tier} back={back} />
}

function TierChange({ tier, back }: { tier: string; back: () => void }) {
  const { api, platform, toast, now } = useEnv()
  const navigate = useNavigate()
  const client = useQueryClient()
  const sub = useSubscription()
  const preview = useTierPreview(tier)
  const history = usePaymentHistory()
  const plan = planOf(tier)!
  const [autoRenewal, setAutoRenewal] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmFree, setConfirmFree] = useState(false)
  /** Сервер не создал платёж: прежний за этот тариф ещё ждёт банка. */
  const [blockedBy, setBlockedBy] = useState<string | null>(null)
  const [pending] = useState(() => {
    const p = loadPendingPayment(now().getTime())
    return p && p.tier === tier ? p : null
  })

  const renewal = sub.data?.paidTier === tier && tier !== 'free'
  const title = renewal ? 'Продление подписки' : 'Смена тарифа'

  if (sub.isPending || preview.isPending) {
    return (
      <Screen title={title} back={back}>
        <Skeleton rows={4} label="Считаю условия" />
      </Screen>
    )
  }
  if (sub.isError || preview.isError) {
    return (
      <Screen title={title} back={back}>
        <ErrorState
          title="Не удалось рассчитать смену тарифа"
          onRetry={() => {
            void sub.refetch()
            void preview.refetch()
          }}
        />
      </Screen>
    )
  }
  const s = sub.data
  const p = preview.data
  if (!s.canManage) {
    return (
      <Screen title={title} back={back}>
        <EmptyState title="Сменить тариф может владелец клуба" text="Посмотреть условия тарифов можно в разделе «Тарифы»." />
      </Screen>
    )
  }

  const over = overLimits(s, plan, p)
  const percent = s.discount?.percent ?? 0
  const amount = discounted(p.charge, percent)
  const paymentsOff = history.data?.available === false

  const applyFree = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await api.applyFreeTierChange(tier)
      await client.invalidateQueries({ queryKey: ['redesign'] })
      platform.haptic('success')
      toast(p.kind === 'trial' ? `Пробный период ${plan.name} начался` : `Тариф ${plan.name} подключён`)
      navigate('/club/subscription', { replace: true })
    } catch (e) {
      setError(changeErrorText(e))
      setConfirmFree(false)
      platform.haptic('error')
      if ((e as SliceApiError)?.kind === 'conflict') void preview.refetch()
      setBusy(false)
    }
  }

  const pay = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    setBlockedBy(null)
    try {
      // PWA: страница оплаты вернёт сюда же (маршрут сам найдёт платёж на
      // устройстве). Мини-приложения: адрес возврата по умолчанию сервера -
      // оплата идёт во внешнем браузере, а статус проверяется при возврате.
      const returnUrl = platform.kind === 'web' ? `${window.location.origin}/club/subscription/payment/return` : ''
      const res = await api.createTierPayment(tier, { autoRenewal, returnUrl })
      savePendingPayment({ id: res.paymentId, tier, url: res.confirmationUrl, createdAt: now().toISOString() })
      navigate(`/club/subscription/payment/${res.paymentId}`, { replace: true })
      // Если мессенджер не откроет страницу без нового нажатия - на экране
      // платежа есть кнопка «Открыть страницу оплаты».
      platform.openPayment(res.confirmationUrl)
    } catch (e) {
      setError(changeErrorText(e))
      platform.haptic('error')
      const err = e as SliceApiError
      if (err?.message === 'payment_pending' && err.paymentId) setBlockedBy(err.paymentId)
      else if (err?.kind === 'conflict') void preview.refetch()
      setBusy(false)
    }
  }

  let action: ReactNode = null
  if (p.kind === 'trial') {
    action = (
      <button type="button" className="pc-btn pc-btn--primary pc-btn--block" disabled={busy} onClick={applyFree}>
        {busy ? 'Подключаю…' : 'Начать пробный период'}
      </button>
    )
  } else if (p.kind === 'free') {
    action = (
      <button type="button" className="pc-btn pc-btn--block" disabled={busy} onClick={() => setConfirmFree(true)}>
        Перейти на {plan.name}
      </button>
    )
  } else if (!paymentsOff) {
    action = (
      <button type="button" className="pc-btn pc-btn--primary pc-btn--block" disabled={busy} onClick={pay}>
        {busy ? 'Создаю платёж…' : `Перейти к оплате ${rub(amount)}`}
      </button>
    )
  }

  return (
    <Screen title={title} back={back} actionBar={action ?? undefined}>
      <p style={{ margin: '12px 0 0' }}>
        {renewal ? (
          <>
            Тариф <strong>{plan.name}</strong>
          </>
        ) : (
          <>
            Сейчас <strong>{tierName(s.paidTier)}</strong>, новый тариф <strong>{plan.name}</strong>
          </>
        )}
      </p>

      {error && (
        <div className="pc-alert" role="alert" style={blockedBy ? { display: 'flex', alignItems: 'center', gap: 12 } : undefined}>
          <span style={{ flex: 1 }}>{error}</span>
          {blockedBy && (
            <button type="button" className="pc-btn" onClick={() => navigate(`/club/subscription/payment/${blockedBy}`)}>
              Проверить
            </button>
          )}
        </div>
      )}

      <div className="pc-card" style={{ marginTop: 12, padding: '4px 12px' }}>
        <PreviewDetails s={s} p={p} amount={amount} percent={percent} />
      </div>

      {p.kind === 'payment' && !paymentsOff && (
        <label className="pc-ack pc-ack--plain">
          <input type="checkbox" checked={autoRenewal} onChange={(e) => setAutoRenewal(e.target.checked)} />
          <span>
            Автопродление: сохранить карту и списывать {rub(plan.price)} каждые 30 дней. Выключается в «Подписке клуба».
          </span>
        </label>
      )}
      {p.kind === 'payment' && paymentsOff && (
        <p className="pc-note">Оплата картой сейчас не подключена: оплатить этот тариф пока нельзя.</p>
      )}
      {p.kind === 'payment' && pending && (
        <div className="pc-note" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1 }}>
            Платёж за {plan.name} от {timeLabel(pending.createdAt)} ещё не подтверждён. Второй за этот тариф не создаётся: оплата
            с теми же условиями откроет этот же платёж.
          </span>
          <button type="button" className="pc-btn" onClick={() => navigate(`/club/subscription/payment/${pending.id}`)}>
            Проверить
          </button>
        </div>
      )}

      <section className="pc-section" aria-labelledby="pc-change-limits">
        <h2 id="pc-change-limits" className="pc-section-title">
          Лимиты
        </h2>
        <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
          {s.limits.map((l) => {
            let max = planLimit(plan, l.key)
            if (l.key === 'ai' && p.kind === 'trial' && max !== -1) max = Math.min(max, TRIAL_AI_REQUESTS)
            const isOver = max !== -1 && l.used > max
            return (
              <li key={l.key}>
                <Row
                  title={LIMIT_LABEL[l.key]}
                  meta={
                    <>
                      Сейчас {l.used} из {limitText(l.max)}, на {plan.name}: {limitText(max)}
                      {isOver && <strong style={{ color: 'var(--pc-danger)' }}>. Больше лимита на {l.used - max}</strong>}
                    </>
                  }
                />
              </li>
            )
          })}
        </ul>
      </section>

      {over.length > 0 && (
        <div className="pc-alert" role="note" style={{ display: 'block' }}>
          <p style={{ margin: 0 }}>Сейчас больше, чем позволит {plan.name}:</p>
          <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
            {over.map((o) => (
              <li key={o.key}>
                {LIMIT_LABEL[o.key]}: {o.used} при лимите {o.max}
                {o.key === 'members' ? ' - никто не удаляется, новых не пригласить' : ''}
                {o.key === 'ai' ? ' - Штаб ответит в следующем месяце' : ''}
              </li>
            ))}
          </ul>
          {over.some((o) => o.key !== 'members' && o.key !== 'ai') && <p style={{ margin: 0 }}>{OVER_LIMIT_RULE}</p>}
        </div>
      )}

      {confirmFree && (
        <Sheet title={`Перейти на ${plan.name}?`} onClose={() => setConfirmFree(false)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            {freeLossText(s)} Лимиты {plan.name} начнут действовать сразу.
          </p>
          <button type="button" className="pc-btn pc-btn--danger pc-btn--block" disabled={busy} onClick={applyFree}>
            {busy ? 'Перехожу…' : `Перейти на ${plan.name}`}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setConfirmFree(false)}>
            Отмена
          </button>
        </Sheet>
      )}
    </Screen>
  )
}

/** Что теряется при переходе на FREE: оплаченный срок не возвращается и не переносится. */
function freeLossText(s: SubscriptionSummary): string {
  if (s.state === 'active' && s.expiresAt) {
    return `Оплаченный срок до ${dateLabel(s.expiresAt)} не возвращается и не переносится.`
  }
  if (s.state === 'trial') return 'Пробный период закончится сразу.'
  return 'Тариф сменится сразу.'
}

function PreviewDetails({
  s,
  p,
  amount,
  percent,
}: {
  s: SubscriptionSummary
  p: TierChangePreview
  amount: number
  percent: number
}) {
  if (p.kind === 'trial') {
    return (
      <>
        <Kv label="Пробный период">
          {daysLabel(p.days)}, до {dateLabel(p.expiresAt)}
        </Kv>
        <Kv label="Оплата">Не нужна, карта не нужна</Kv>
        <Kv label="Запросы Штаба">До {TRIAL_AI_REQUESTS} в месяц, пока идёт пробный период</Kv>
        <Kv label="Потом">{TRIAL_AFTER_TEXT}</Kv>
      </>
    )
  }
  if (p.kind === 'free') {
    return (
      <>
        <Kv label="Цена">Бесплатно, без срока</Kv>
        {s.paidTier !== 'free' && <Kv label="Что изменится">{freeLossText(s)}</Kv>}
      </>
    )
  }
  return (
    <>
      <Kv label="К оплате">
        {percent > 0 ? `${rub(amount)} вместо ${rub(p.charge)}, скидка ${percent}% по промокоду` : rub(p.charge)}
      </Kv>
      <Kv label="Срок">
        {daysLabel(p.days)}, до {dateLabel(p.expiresAt)}
      </Kv>
      {p.daysRemaining > 0 && (
        <Kv label="Пересчёт">
          {`Неиспользованные ${daysLabel(p.daysRemaining)} тарифа ${tierName(p.currentTier)} (${rub(Math.round(p.remainingValue))}) пересчитаны в дни нового тарифа и добавлены к сроку`}
        </Kv>
      )}
      {p.trialDaysLost > 0 && <Kv label="Пробный период">{trialLostText(p.trialDaysLost, p.currentTier)}</Kv>}
      <Kv label="Как оплатить">Банковской картой на странице платёжной системы. Тариф сменится после подтверждения оплаты</Kv>
    </>
  )
}

// ---------------------------------------------------------------------------
// Статус платежа: возврат со страницы оплаты
// ---------------------------------------------------------------------------

const POLL_MS = 3000
/** Две минуты активной проверки, дальше - по возврату в приложение и по кнопке. */
const POLL_ATTEMPTS = 40

function isFinal(p: PaymentInfo | null): boolean {
  return !!p && (p.status === 'succeeded' || p.status === 'failed' || p.status === 'canceled')
}

/**
 * Следить за платежом: сразу, затем каждые pollMs (каждая третья проверка -
 * у платёжной системы напрямую, на случай потерянного уведомления), и заново
 * при возврате в приложение: из внешнего браузера (мини-приложения), из
 * встроенного окна браузера поверх установленной PWA (iOS), из другой вкладки.
 */
function usePaymentWatch(id: string, pollMs: number) {
  const { api, platform } = useEnv()
  const [info, setInfo] = useState<PaymentInfo | null>(null)
  const [error, setError] = useState<SliceApiError | null>(null)
  const [watching, setWatching] = useState(true)
  const attempts = useRef(0)
  const inFlight = useRef(false)
  const infoRef = useRef(info)
  infoRef.current = info

  const check = useCallback(
    async (withProvider: boolean) => {
      if (inFlight.current) return
      inFlight.current = true
      try {
        setInfo(await api.getPayment(id, withProvider))
        setError(null)
      } catch (e) {
        setError(e instanceof SliceApiError ? e : new SliceApiError('server error', 'server'))
      } finally {
        inFlight.current = false
      }
    },
    [api, id],
  )

  const restart = useCallback(() => {
    if (isFinal(infoRef.current)) return Promise.resolve()
    attempts.current = 0
    setWatching(true)
    return check(true)
  }, [check])

  useEffect(() => {
    void check(false)
  }, [check])

  const final = isFinal(info)
  useEffect(() => {
    if (!watching || final) return
    const t = setInterval(() => {
      attempts.current += 1
      if (attempts.current > POLL_ATTEMPTS) {
        setWatching(false)
        return
      }
      void check(attempts.current % 3 === 0)
    }, pollMs)
    return () => clearInterval(t)
  }, [watching, final, check, pollMs])

  useEffect(() => {
    let last = 0
    const again = () => {
      const t = Date.now()
      if (t - last < 1500) return
      last = t
      void restart()
    }
    // Мини-приложения: возврат по видимости и событиям моста; PWA - по видимости.
    const off = platform.kind === 'web' ? subscribeResume(again) : platform.onResume(again)
    const onShow = () => again()
    window.addEventListener('focus', onShow)
    window.addEventListener('pageshow', onShow)
    return () => {
      off()
      window.removeEventListener('focus', onShow)
      window.removeEventListener('pageshow', onShow)
    }
  }, [platform, restart])

  return { info, error, watching, checkNow: () => restart() }
}

export function PaymentStatusScreen({ pollMs = POLL_MS }: { pollMs?: number }) {
  const { id = '' } = useParams()
  if (id === 'return') return <PaymentReturn />
  return <PaymentStatusView key={id} id={id} pollMs={pollMs} />
}

/** Возврат со страницы оплаты в PWA: платёж ищем на устройстве. */
function PaymentReturn() {
  const navigate = useNavigate()
  const { now } = useEnv()
  // Срок платежа на устройстве - по часам приложения, как и при сохранении
  const [pending] = useState(() => loadPendingPayment(now().getTime()))
  if (pending) return <Navigate to={`/club/subscription/payment/${pending.id}`} replace />
  return (
    <Screen title="Оплата тарифа" back={() => navigate('/club/subscription', { replace: true })}>
      <EmptyState
        title="Платёж на этом устройстве не найден"
        text="Тариф и платежи видны в разделе «Подписка клуба»."
        action={
          <button type="button" className="pc-btn" onClick={() => navigate('/club/subscription', { replace: true })}>
            К подписке
          </button>
        }
      />
    </Screen>
  )
}

function PaymentStatusView({ id, pollMs }: { id: string; pollMs: number }) {
  const { platform, toast, now } = useEnv()
  const navigate = useNavigate()
  const client = useQueryClient()
  const sub = useSubscription()
  const [pending] = useState(() => {
    const p = loadPendingPayment(now().getTime())
    return p && p.id === id ? p : null
  })
  const { info, error, watching, checkNow } = usePaymentWatch(id, pollMs)
  const toSubscription = () => navigate('/club/subscription', { replace: true })
  const [checking, setChecking] = useState(false)

  // Итог платежа: больше не напоминаем; после успеха - перечитать всё (лимиты
  // меняют поведение других экранов).
  const status = info?.status
  useEffect(() => {
    if (status !== 'succeeded' && status !== 'failed' && status !== 'canceled') return
    clearPendingPayment(id)
    if (status === 'succeeded') {
      platform.haptic('success')
      void client.invalidateQueries({ queryKey: ['redesign'] })
    } else {
      platform.haptic('error')
    }
  }, [status, id, platform, client])

  const notFound = !info && error?.kind === 'not_found'
  useEffect(() => {
    if (notFound) clearPendingPayment(id)
  }, [notFound, id])

  const url = info?.confirmationUrl ?? pending?.url
  const targetTier = pending?.tier

  let body: ReactNode
  if (!info && !error) {
    body = <Skeleton rows={2} label="Проверяю платёж" />
  } else if (!info && notFound) {
    body = (
      <EmptyState
        title="Платёж не найден"
        text="Возможно, его создали с другого аккаунта. Тариф и платежи видны в разделе «Подписка клуба»."
      />
    )
  } else if (!info) {
    body = (
      <ErrorState
        title="Не удалось узнать статус платежа"
        text="Проверьте связь. Если оплата прошла, тариф обновится после подтверждения банка."
        onRetry={() => void checkNow()}
      />
    )
  } else if (info.status === 'succeeded') {
    const applied = !targetTier || sub.data?.paidTier === targetTier
    body = (
      <div className="pc-state" role="status">
        <span className="pc-badge pc-badge--success">Оплачено</span>
        <h3 style={{ marginTop: 8 }}>Оплата прошла</h3>
        <p>
          Списано {rub(info.amount)}.
          {sub.data && applied && sub.data.paidTier !== 'free'
            ? ` Тариф ${tierName(sub.data.paidTier)} действует до ${dateLabel(sub.data.expiresAt)}.`
            : ''}
        </p>
        {sub.data && !applied && (
          <>
            <p>Оплата подтверждена, а тариф пока не обновился. Проверьте ещё раз через минуту.</p>
            <button type="button" className="pc-btn" onClick={() => void sub.refetch()}>
              Проверить тариф
            </button>
          </>
        )}
      </div>
    )
  } else if (info.status === 'failed' || info.status === 'canceled') {
    body = (
      <div className="pc-state" data-tone="error" role="alert">
        <h3>{info.status === 'failed' ? 'Платёж не прошёл' : 'Платёж отменён'}</h3>
        <p>Тариф не изменился. Можно попробовать ещё раз или выбрать другой тариф.</p>
        {targetTier && (
          <button
            type="button"
            className="pc-btn pc-btn--primary"
            onClick={() => navigate(`/club/subscription/change/${targetTier}`, { replace: true })}
          >
            Попробовать ещё раз
          </button>
        )}
      </div>
    )
  } else {
    body = (
      <div role="status" aria-live="polite">
        <h2 className="pc-section-title" style={{ marginTop: 16 }}>
          {info.status === 'unknown' ? 'Статус платежа пока неизвестен' : 'Ждём подтверждения оплаты'}
        </h2>
        <p className="pc-muted" style={{ margin: 0 }}>
          Сумма: {rub(info.amount)}
          {targetTier ? `, тариф ${tierName(targetTier)}` : ''}.
        </p>
        <p style={{ margin: '8px 0 0', fontSize: 14 }}>
          {platform.kind === 'web'
            ? 'Завершите оплату на странице платёжной системы: после оплаты она вернёт сюда, статус проверится сам.'
            : 'Страница оплаты открыта в браузере. После оплаты вернитесь в приложение: статус проверится сам.'}
        </p>
        {!watching && (
          <p className="pc-note">
            Банк пока не подтвердил платёж. Если оплата прошла, тариф обновится после подтверждения - проверить можно здесь
            в любой момент.
          </p>
        )}
        {error && (
          <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
            Не удалось проверить: {error.kind === 'network' ? 'нет связи' : 'сбой сервера'}. Проверка продолжится.
          </p>
        )}
        {url && (
          <button
            type="button"
            className="pc-btn pc-btn--primary pc-btn--block"
            style={{ marginTop: 12 }}
            onClick={() => {
              if (!platform.openPayment(url)) toast('Не удалось открыть страницу оплаты')
            }}
          >
            Открыть страницу оплаты
          </button>
        )}
        <button
          type="button"
          className="pc-btn pc-btn--block"
          style={{ marginTop: 8 }}
          disabled={checking}
          onClick={async () => {
            setChecking(true)
            await checkNow()
            setChecking(false)
          }}
        >
          {checking ? 'Проверяю…' : 'Проверить сейчас'}
        </button>
      </div>
    )
  }

  return (
    <Screen title="Оплата тарифа" back={toSubscription}>
      {body}
      <button type="button" className="pc-btn pc-btn--link" style={{ marginTop: 16 }} onClick={toSubscription}>
        К подписке клуба
      </button>
    </Screen>
  )
}
